import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { log } from '@/lib/logger';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';
import { mixpanel } from '@/lib/analytics/mixpanel';
import { typedRpcValue, typedSelect } from '@/lib/supabaseTypedQuery';
import { memberRemovalErrorMessage } from '@/lib/memberRemovalError';
import type { MemberHandover } from '@/hooks/useMemberHandovers';
import { queryClient } from '@/lib/queryClient';
import { activeWorkspaceStorageKey, persistActiveWorkspaceSelection } from '@/lib/activeWorkspaceSelection';
import { fetchMainWorkspaceId } from '@/lib/workspaceResolver';
import { chooseActiveMembership, needsMainWorkspace } from '@/lib/workspaceSelection';
import { claimSsoMembership, isSsoUser } from '@/lib/ssoMembership';
import { requestVerificationFor } from '@/lib/verificationPrompt';
import { getErrorMessage } from '@/lib/error-utils';
import { queryKeys } from '@/lib/queryKeys';
import { activateBulkEmailDraftSession } from '@/lib/bulkEmailDraftStorage';
import { setViewOnlySession } from '@/lib/viewOnlySession';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
  plan: 'solo' | 'team' | 'enterprise';
  subscription_status: string;
  max_users: number;
  created_at: string;
  updated_at: string;
  partner_id?: string | null;
  partner_display_name?: string | null;
  partner_logo_url?: string | null;
  partner_acknowledged_at?: string | null;
  /** Members must pass two-step verification (or this workspace's SSO) to use it. */
  require_mfa?: boolean;
  /** Saved team reporting currency, independent of personal profile settings. */
  currency?: string;
  /** Firm default: new recorded calls are shared with every member when they arrive. */
  share_recorded_calls?: boolean;
  commercial_tier_bands?: import('@/lib/companyCommercialAssessment').CommercialBands;
  commercial_tier_bands_version?: number;
  /** The team's shared IANA time zone; null when each member uses their own. */
  timezone?: string | null;
  timezone_explicitly_unset?: boolean;
}

export interface WorkspaceMember {
  id: string;
  user_id: string;
  workspace_id: string;
  role: 'owner' | 'admin' | 'member' | 'viewer';
  is_support: boolean;
  joined_at: string;
  user: {
    id: string;
    email: string;
    name?: string;
  };
}

export interface WorkspaceMembershipOption {
  workspace_id: string;
  role: WorkspaceMember['role'];
  workspace: Workspace;
}

interface WorkspaceContextType {
  accountUserId: string | null;
  workspace: Workspace | null;
  workspaces: WorkspaceMembershipOption[];
  members: WorkspaceMember[];
  userRole: 'owner' | 'admin' | 'member' | 'viewer' | null;
  loading: boolean;
  setActiveWorkspace: (workspaceId: string) => Promise<void>;
  refreshWorkspace: () => Promise<void>;
  inviteMember: (email: string, role: string) => Promise<boolean>;
  /** Removes a teammate and hands their records to the named successor. */
  removeMember: (userId: string, successorUserId: string) => Promise<MemberHandover | null>;
  updateMemberRole: (userId: string, role: string) => Promise<void>;
  transferOwnership: (userId: string) => Promise<boolean>;
  /** Leaves the workspace, handing your records to the named successor. */
  leaveWorkspace: (successorUserId: string) => Promise<boolean>;
}

// eslint-disable-next-line react-refresh/only-export-components
export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within WorkspaceProvider');
  }
  return context;
};

/** Safe version that returns null outside WorkspaceProvider instead of throwing */
// eslint-disable-next-line react-refresh/only-export-components
export const useOptionalWorkspace = () => {
  return useContext(WorkspaceContext) ?? null;
};

/** Reset record UI only on a real tenant change, not a refreshed snapshot.
 * Keep persistence providers outside this boundary: their unmount cleanup
 * can flush pending writes and must not run merely to reset a record view.
 */
export function WorkspaceViewBoundary({ children }: { children: ReactNode }) {
  const { accountUserId, workspace } = useWorkspace();
  return <React.Fragment key={JSON.stringify([accountUserId, workspace?.id ?? null])}>{children}</React.Fragment>;
}

interface WorkspaceProviderProps {
  children: ReactNode;
  user: User | null;
  /** A checkout-return target that wins over a stale preference only when membership is verified. */
  preferredWorkspaceId?: string;
}

const activeWorkspaceKey = activeWorkspaceStorageKey;

interface WorkspaceMemberRow {
  id: string;
  user_id: string;
  workspace_id: string;
  role: string;
  is_support: boolean | null;
  joined_at: string;
}

interface WorkspaceProfileRow {
  id: string;
  name: string | null;
  email: string | null;
}

interface WorkspaceMembershipRow {
  workspace_id: string;
  role: string;
  joined_at: string;
  workspaces: Workspace | null;
}

const normalizeWorkspaceRole = (role: string): WorkspaceMember['role'] => {
  if (role === 'owner' || role === 'admin' || role === 'member' || role === 'viewer') {
    return role;
  }
  // Only 'viewer' is read-only, in the database as here. Any other role
  // ('manager' is in the enum, unused today) writes there, so it edits here.
  log.warn('Unknown workspace role treated as member', { role });
  return 'member';
};

const toWorkspaceOption = (membership: WorkspaceMembershipRow): WorkspaceMembershipOption | null => {
  if (!membership.workspaces) return null;

  return {
    workspace_id: membership.workspace_id,
    role: normalizeWorkspaceRole(membership.role),
    workspace: membership.workspaces,
  };
};

/** Fetch and format workspace members for a given workspace */
async function fetchWorkspaceMembers(workspaceId: string): Promise<WorkspaceMember[]> {
  // Step 1: Fetch workspace members (no join — FK to profiles is indirect via auth.users)
  const { data: membersData, error: membersError } = await typedSelect<WorkspaceMemberRow>(
    'workspace_members',
    'id, user_id, workspace_id, role, is_support, joined_at'
  )
    .eq('workspace_id', workspaceId);

  if (membersError) throw membersError;
  if (!membersData || membersData.length === 0) return [];

  // Step 2: Fetch profiles for all member user_ids in a single query
  const userIds = membersData.map(m => m.user_id);
  const { data: profilesData, error: profilesError } = await typedSelect<WorkspaceProfileRow>(
    'profiles',
    'id, name, email'
  )
    .in('id', userIds);

  if (profilesError) {
    log.warn('fetchWorkspaceMembers: failed to fetch profiles, proceeding without', { error: profilesError });
  }

  // Build a lookup map: user_id → profile
  const profileMap = new Map<string, WorkspaceProfileRow>();
  if (profilesData) {
    for (const p of profilesData) {
      profileMap.set(p.id, p);
    }
  }

  return membersData.map(m => {
    const profile = profileMap.get(m.user_id);
    return {
      id: m.id,
      user_id: m.user_id,
      workspace_id: m.workspace_id,
      role: normalizeWorkspaceRole(m.role),
      is_support: m.is_support === true,
      joined_at: m.joined_at,
      user: {
        id: profile?.id ?? m.user_id,
        email: profile?.email || '',
        name: profile?.name ?? undefined } };
  });
}

export const WorkspaceProvider: React.FC<WorkspaceProviderProps> = ({ children, user, preferredWorkspaceId }) => {
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [workspaceOptions, setWorkspaceOptions] = useState<WorkspaceMembershipOption[]>([]);
  const [members, setMembers] = useState<WorkspaceMember[]>([]);
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'member' | 'viewer' | null>(null);
  const [loading, setLoading] = useState(true);
  const [resolvedUserId, setResolvedUserId] = useState<string | null>(null);
  // The provider survives route changes. Clear drafts only when their actual
  // account/workspace identity changes, not when a page's composer unmounts.
  const emailDraftScope = user?.id && resolvedUserId === user.id && workspace?.id
    ? `${user.id}:${workspace.id}` : null;
  useEffect(() => { activateBulkEmailDraftSession(emailDraftScope); }, [emailDraftScope]);
  // Publish view-only for the shell outside this provider (Assist, command bar).
  useEffect(() => {
    setViewOnlySession(Boolean(workspace) && userRole === 'viewer');
  }, [workspace, userRole]);
  useEffect(() => () => setViewOnlySession(false), []);
  const resolvedUserIdRef = useRef<string | null>(null);
  const activeWorkspaceIdRef = useRef<string | null>(null);
  // Ref for recursive self-call inside loadWorkspace (auto-create retry)
  const loadWorkspaceRef = useRef<() => Promise<void>>();
  // Serial counter: each call claims a slot; stale calls (superseded by a newer
  // concurrent invocation) skip state mutations so the last caller always wins.
  const loadRequestIdRef = useRef(0);

  const loadWorkspace = useCallback(async () => {
    if (!user) {
      ++loadRequestIdRef.current;
      resolvedUserIdRef.current = null;
      activeWorkspaceIdRef.current = null;
      setResolvedUserId(null);
      setWorkspace(null);
      setWorkspaceOptions([]);
      setMembers([]);
      setUserRole(null);
      setLoading(false);
      return;
    }

    const myId = ++loadRequestIdRef.current;
    const isStale = () => myId !== loadRequestIdRef.current;
    // A refreshed auth user is a new object for the same account. Revalidate
    // membership without replacing the app shell (and losing local drafts).
    // Explicit switches still set loading before calling this function.
    if (resolvedUserIdRef.current !== user.id) {
      setLoading(true);
      if (resolvedUserIdRef.current !== null) queryClient.clear();
      activeWorkspaceIdRef.current = null;
      setResolvedUserId(null);
      setWorkspace(null);
      setWorkspaceOptions([]);
      setMembers([]);
      setUserRole(null);
    }

    try {
      // An SSO sign-in joins its company workspace before memberships load, and
      // a first join selects it, like an accepted invitation. Otherwise a new
      // SSO user opens their empty Solo workspace and hits the paywall.
      if (isSsoUser(user)) {
        const ssoClaim = await claimSsoMembership(user);
        // Persist before the staleness check: a newer load would see
        // 'already_member' and never select the workspace this join created.
        if (ssoClaim?.status === 'joined' && ssoClaim.workspace_id) {
          persistActiveWorkspaceSelection(user.id, ssoClaim.workspace_id);
        }
        if (isStale()) return;
      }

      // Persistent support seats are retired. Normal sessions must never load
      // a customer workspace through a legacy is_support membership.
      const membershipResult = await typedSelect<WorkspaceMembershipRow>(
        'workspace_members',
        'workspace_id, role, joined_at, workspaces(*)'
      )
        .eq('user_id', user.id)
        .eq('is_support', false)
        .order('role', { ascending: true })
        .order('joined_at', { ascending: true });

      if (isStale()) return;
      const { data: memberships, error: membershipError } = membershipResult;
      // PostgREST puts HTTP status on the response, not necessarily its error.
      const membershipStatus = (membershipResult as { status?: number }).status;
      if (membershipStatus === 401 || membershipStatus === 403) {
        throw { ...membershipError, status: membershipStatus };
      }

      // No workspace memberships found — user needs a workspace
      if (!membershipError && (!memberships || memberships.length === 0)) {
        // Confirmed revocation is not a transient refresh failure. Remove the
        // old workspace before auto-creation can wait on another network call.
        if (activeWorkspaceIdRef.current !== null) queryClient.clear();
        activeWorkspaceIdRef.current = null;
        setLoading(true);
        setWorkspace(null);
        setWorkspaceOptions([]);
        setMembers([]);
        setUserRole(null);
        // Tiered cooldown: transient errors (network, timeout) get a short 30-second
        // cooldown. Permanent errors (auth, permission) get the full 30-minute cooldown.
        // This prevents Sentry storms while not locking users out after a network blip.
        const cooldownKey = `nynch:workspace-create-failed:${user.id}`;
        const cooldownTypeKey = `nynch:workspace-create-error-type:${user.id}`;
        const lastFailure = localStorage.getItem(cooldownKey);
        const lastErrorType = localStorage.getItem(cooldownTypeKey) || 'permanent';
        const TRANSIENT_COOLDOWN_MS = 30 * 1000; // 30 seconds for network/timeout errors
        const PERMANENT_COOLDOWN_MS = 30 * 60 * 1000; // 30 minutes for auth/permission errors
        const cooldownMs = lastErrorType === 'transient' ? TRANSIENT_COOLDOWN_MS : PERMANENT_COOLDOWN_MS;
        if (lastFailure && Date.now() - Number(lastFailure) < cooldownMs) {
          log.debug('Workspace auto-create on cooldown, skipping', { errorType: lastErrorType, cooldownMs });
          setWorkspace(null);
          setWorkspaceOptions([]);
          setMembers([]);
          setUserRole(null);
          return;
        }

        log.info('User has no workspace membership — auto-creating workspace');
        try {
          const result = await invokeEdgeFunction<{ ok: boolean; workspace_id: string }>('setup-workspace', {
            body: { withSample: false } });
          if (isStale()) return;
          if (result?.ok && result?.workspace_id) {
            log.info('Auto-created workspace', { workspaceId: result.workspace_id });
            localStorage.removeItem(cooldownKey);
            localStorage.removeItem(cooldownTypeKey);
            // Retry loading now that workspace exists
            await loadWorkspaceRef.current?.();
            return;
          }
        } catch (setupError) {
          if (isStale()) return;
          const errMsg = (setupError instanceof Error ? setupError.message : String(setupError)).toLowerCase();

          // Auth / session errors are expected during login hydration — the user
          // object exists (from cached localStorage session) but the JWT may be
          // expired or the session may not be fully established yet.
          const isAuthError = errMsg.includes('session expired') || errMsg.includes('sign in')
            || errMsg.includes('jwt') || errMsg.includes('not authenticated')
            || errMsg.includes('unauthorized') || errMsg.includes('permission denied');

          // Transient errors: network failures, timeouts, rate limits, edge function
          // boot errors. These should retry quickly — a 2-second network blip shouldn't
          // lock a user out for 30 minutes.
          const isTransientError = errMsg.includes('network') || errMsg.includes('timeout')
            || errMsg.includes('rate limit') || errMsg.includes('429')
            || errMsg.includes('503') || errMsg.includes('502')
            || errMsg.includes('fetch') || errMsg.includes('relay error')
            || errMsg.includes('boot error');

          if (isAuthError) {
            log.info('Workspace auto-create skipped (auth)', { error: errMsg });
            localStorage.setItem(cooldownTypeKey, 'permanent');
          } else if (isTransientError) {
            const detail = setupError instanceof Error ? setupError.message : String(setupError);
            log.info('Workspace auto-create failed (transient)', { error: detail });
            localStorage.setItem(cooldownTypeKey, 'transient');
          } else {
            const detail = setupError instanceof Error ? setupError.message : String(setupError);
            log.warn('Failed to auto-create workspace', { error: detail });
            localStorage.setItem(cooldownTypeKey, 'permanent');
          }
          localStorage.setItem(cooldownKey, String(Date.now()));
        }
        // Auto-create failed or on cooldown — no workspace available
        setWorkspace(null);
        setWorkspaceOptions([]);
        setMembers([]);
        setUserRole(null);
        return;
      }
      if (membershipError) throw membershipError;

      if (memberships && memberships.length > 0) {
        const options = memberships.flatMap(m => {
          const option = toWorkspaceOption(m);
          return option ? [option] : [];
        });

        setWorkspaceOptions(options);

        const preferredMembership = preferredWorkspaceId
          ? memberships.find(m => m.workspace_id === preferredWorkspaceId && m.workspaces)
          : undefined;
        if (preferredWorkspaceId && !preferredMembership) {
          if (activeWorkspaceIdRef.current !== null) queryClient.clear();
          activeWorkspaceIdRef.current = null;
          log.warn('Checkout workspace is not an active membership; refusing fallback selection', {
            preferredWorkspaceId,
          });
          setWorkspace(null);
          setMembers([]);
          setUserRole(null);
          return;
        }
        const savedWorkspaceId = localStorage.getItem(activeWorkspaceKey(user.id));
        const choice = { preferredWorkspaceId, savedWorkspaceId };
        // Several workspaces and no checkout or saved choice: the person's main
        // workspace under the one rule. A failed lookup throws to the load's error
        // handler, which shows a failure and opens nothing rather than guessing.
        const mainWorkspaceId = needsMainWorkspace(memberships, choice)
          ? await fetchMainWorkspaceId(user.id)
          : null;
        if (isStale()) return;
        const activeMembership = chooseActiveMembership(memberships, { ...choice, mainWorkspaceId });

        if (!activeMembership?.workspaces) {
          if (activeWorkspaceIdRef.current !== null) queryClient.clear();
          activeWorkspaceIdRef.current = null;
          setWorkspace(null);
          setMembers([]);
          setUserRole(null);
          return;
        }

        if (memberships.length > 1) {
          // Only a checkout is remembered. The main-workspace default is re-derived
          // on each load, so it follows the rule rather than freezing the first answer.
          if (preferredMembership) {
            localStorage.setItem(activeWorkspaceKey(user.id), activeMembership.workspace_id);
          }
          log.info('User belongs to multiple workspaces, using active workspace', {
            count: memberships.length,
            selectedWorkspaceId: activeMembership.workspace_id,
            role: activeMembership.role,
          });
        }

        if (activeWorkspaceIdRef.current !== null
          && activeWorkspaceIdRef.current !== activeMembership.workspace_id) {
          // A removed membership can select a different valid workspace even
          // without an explicit switch. Do not retain the previous view/cache.
          setLoading(true);
          queryClient.clear();
          setMembers([]);
        }
        activeWorkspaceIdRef.current = activeMembership.workspace_id;
        setWorkspace(activeMembership.workspaces);
        setUserRole(normalizeWorkspaceRole(activeMembership.role));
        const formattedMembers = await fetchWorkspaceMembers(activeMembership.workspace_id);
        if (isStale()) return;
        setMembers(formattedMembers);
      }
    } catch (error) {
      if (!isStale()) {
        const failure = error as { code?: string; status?: number; statusCode?: number } | null;
        if (['42501', '28000', '28P01', 'PGRST301', 'PGRST302', 'PGRST303'].includes(failure?.code ?? '')
          || [401, 403].includes(Number(failure?.status))
          || [401, 403].includes(Number(failure?.statusCode))) {
          // Confirmed access loss invalidates the mounted tenant view as well
          // as its cached records. Offline refresh failures retain both.
          activeWorkspaceIdRef.current = null;
          setWorkspace(null);
          setWorkspaceOptions([]);
          setMembers([]);
          setUserRole(null);
          queryClient.clear();
        }
        log.error('Error loading workspace', { error });
        toast.error('Failed to load workspace');
      }
    } finally {
      if (!isStale()) {
        resolvedUserIdRef.current = user.id;
        setResolvedUserId(user.id);
        setLoading(false);
      }
    }
  }, [preferredWorkspaceId, user]);
  loadWorkspaceRef.current = loadWorkspace;

  useEffect(() => {
    loadWorkspace();
    return () => { ++loadRequestIdRef.current; };
  }, [user, loadWorkspace]);

  // The legacy workspace context is not a Query-owned snapshot. Bridge the
  // shared billing reconciliation result into this context once per request,
  // including failed runs that durably saved partial recovery. Account-scoped
  // query keys prevent a previous login's completion from refreshing this one.
  useEffect(() => {
    if (!user) return;
    const syncKey = JSON.stringify(queryKeys.subscription.sync(user.id));
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type !== 'updated'
        || !['success', 'error'].includes(event.action.type)
        || JSON.stringify(event.query.queryKey) !== syncKey) return;
      void loadWorkspaceRef.current?.();
    });
  }, [user]);

  const refreshWorkspace = async () => {
    await loadWorkspace();
  };

  // NOTE: do NOT pre-validate against workspaceOptions here. That state can be
  // stale when a membership was created moments ago (support console "Join &
  // Open" joins a workspace and immediately switches to it — the old guard
  // silently refused the switch and left the user in their own workspace).
  // loadWorkspace() already validates: it only honors the saved workspace id
  // when it appears in the freshly fetched memberships, and falls back to the
  // main workspace under the one rule otherwise — so a bogus id can never grant access.
  const setActiveWorkspace = useCallback(async (workspaceId: string) => {
    if (!user) return;

    // Drop all React Query cache before the switch so workspace-A CRM rows never
    // flash under workspace-B. Mirrors AuthContext SIGNED_OUT — full clear is the
    // safe cross-tenant boundary. Skip when re-selecting the already-active id.
    if (workspace?.id !== workspaceId) {
      queryClient.clear();
    }

    localStorage.setItem(activeWorkspaceKey(user.id), workspaceId);
    if (workspace?.id !== workspaceId) setLoading(true);
    await loadWorkspace();
  }, [loadWorkspace, user, workspace?.id]);

  const inviteMember = async (email: string, role: string) => {
    if (!workspace) return false;

    try {
      const data = await invokeEdgeFunction<{ success?: boolean; error?: string }>(
        'send-workspace-invitation',
        {
          body: {
            email,
            workspaceId: workspace.id,
            role,
            inviterName: user?.email,
            workspaceName: workspace.name } }
      );

      if (data?.success) {
        toast.success(`Invitation sent to ${email}`);
        mixpanel.trackPQLEvent('team_invited', { role });
        await refreshWorkspace();
        return true;
      } else {
        throw new Error(data?.error || 'Failed to send invitation');
      }
    } catch (error) {
      log.error('Error inviting member', { error });
      toast.error(error instanceof Error ? error.message : 'Failed to send invitation');
      return false;
    }
  };

  const removeMember = async (userId: string, successorUserId: string) => {
    if (!workspace || !userRole || !['owner', 'admin'].includes(userRole)) {
      toast.error("You don't have permission to remove members");
      return null;
    }

    try {
      // Access ends and the handover ledger opens in one transaction; the
      // records themselves then move in bounded batches.
      const { data, error } = await typedRpcValue<{ removed: boolean; handover: MemberHandover | null }>(
        'app_remove_workspace_member',
        {
          p_workspace_id: workspace.id,
          p_target_user_id: userId,
          p_successor_user_id: successorUserId,
        },
      );

      if (error) throw error;

      toast.success('Removed from the workspace. Their records are on their way to the teammate you chose.');
      await refreshWorkspace();
      return data?.handover ?? null;
    } catch (error) {
      log.error('Error removing member', { error });
      if (requestVerificationFor(error, 'remove the member')) return null;
      toast.error(memberRemovalErrorMessage(error, 'Failed to remove member'));
      return null;
    }
  };

  const updateMemberRole = async (userId: string, role: string) => {
    if (!workspace || !userRole || !['owner', 'admin'].includes(userRole)) {
      toast.error("You don't have permission to update roles");
      return;
    }

    try {
      const { error } = await supabase.rpc('app_update_workspace_member_role' as never, {
        p_workspace_id: workspace.id,
        p_target_user_id: userId,
        p_role: role,
      } as never);

      if (error) throw error;

      toast.success('Member role updated');
      await refreshWorkspace();
    } catch (error) {
      log.error('Error updating member role', { error });
      if (requestVerificationFor(error, 'change the role')) return;
      toast.error(getErrorMessage(error, 'Failed to update member role'));
    }
  };

  const transferOwnership = async (userId: string) => {
    if (!workspace || userRole !== 'owner') {
      toast.error("You don't have permission to transfer ownership");
      return false;
    }

    try {
      const { error } = await supabase.rpc('app_transfer_workspace_ownership' as never, {
        p_workspace_id: workspace.id,
        p_new_owner_id: userId,
      } as never);

      if (error) throw error;

      toast.success('Workspace ownership transferred');
      await refreshWorkspace();
      return true;
    } catch (error) {
      log.error('Error transferring workspace ownership', { error });
      if (requestVerificationFor(error, 'transfer ownership')) return false;
      toast.error(getErrorMessage(error, 'Failed to transfer ownership'));
      return false;
    }
  };

  const leaveWorkspace = async (successorUserId: string) => {
    if (!workspace) return false;

    try {
      // Hands everything you own here to the teammate you chose. The database
      // sweep finishes the handover after you have gone.
      const { error } = await typedRpcValue<{ left: boolean }>('app_leave_workspace', {
        p_workspace_id: workspace.id,
        p_successor_user_id: successorUserId,
      });

      if (error) throw error;

      if (user) {
        localStorage.removeItem(activeWorkspaceKey(user.id));
      }
      toast.success(`You left ${workspace.name}`);
      await refreshWorkspace();
      return true;
    } catch (error) {
      log.error('Error leaving workspace', { error });
      toast.error(memberRemovalErrorMessage(error, 'Failed to leave workspace'));
      return false;
    }
  };

  const accountResolved = !!user && resolvedUserId === user.id;

  return (
    <WorkspaceContext.Provider
      value={{
        accountUserId: accountResolved ? resolvedUserId : null,
        workspace: accountResolved ? workspace : null,
        workspaces: accountResolved ? workspaceOptions : [],
        members: accountResolved ? members : [],
        userRole: accountResolved ? userRole : null,
        loading: !!user && (!accountResolved || loading),
        setActiveWorkspace,
        refreshWorkspace,
        inviteMember,
        removeMember,
        updateMemberRole,
        transferOwnership,
        leaveWorkspace }}
    >
      {children}
    </WorkspaceContext.Provider>
  );
};
